import React from "react";
import StatusBadge from "./StatusBadge";

type Column<T> = {
  key: keyof T | string;
  header: string;
  render?: (row: T) => React.ReactNode;
  align?: "left" | "right" | "center";
};

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  error?: string | null;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (row: T) => void;
}

const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  error = null,
  page = 1,
  pageSize = 20,
  total,
  onPageChange,
  onRowClick,
}: DataTableProps<T>) => {
  const pageCount = total ? Math.ceil(total / pageSize) : 1;

  return (
    <div className="overflow-hidden rounded-xl border border-surface-border bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-surface-border text-sm">
          <thead className="bg-surface-alt text-left text-xs uppercase tracking-wider text-muted">
            <tr>
              {columns.map((col) => (
                <th key={col.key as string} className="px-4 py-3 font-semibold">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="animate-pulse">
                  {columns.map((col, idx) => (
                    <td key={`${col.key as string}-${idx}`} className="px-4 py-3">
                      <div className="h-3 rounded bg-surface-alt" />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading && error && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-rose-600">
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && data.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-center text-muted">
                  データがありません
                </td>
              </tr>
            )}

            {!loading && !error &&
              data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-surface-alt cursor-pointer"
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((col) => (
                    <td
                      key={`${col.key as string}-${rowIndex}`}
                      className={`px-4 py-3 ${
                        col.align === "right"
                          ? "text-right"
                          : col.align === "center"
                          ? "text-center"
                          : "text-left"
                      }`}
                    >
                      {col.render ? col.render(row) : String(row[col.key as keyof T] ?? "-")}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {total && total > pageSize && (
        <div className="flex items-center justify-between border-t border-surface-border bg-surface-alt px-4 py-3 text-xs text-muted">
          <div>
            {page} / {pageCount} ページ
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded-lg border border-surface-border px-2 py-1 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
            >
              前へ
            </button>
            <button
              className="rounded-lg border border-surface-border px-2 py-1 disabled:opacity-40"
              disabled={page >= pageCount}
              onClick={() => onPageChange?.(page + 1)}
            >
              次へ
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SampleStatusCell = ({ status }: { status: string }) => {
  const normalized = status.toLowerCase();
  if (normalized === "approved" || normalized === "published") {
    return <StatusBadge label={status} tone="green" />;
  }
  if (normalized === "pending") return <StatusBadge label={status} tone="yellow" />;
  if (normalized === "rejected" || normalized === "failed")
    return <StatusBadge label={status} tone="red" />;
  return <StatusBadge label={status} tone="gray" />;
};

export default DataTable;
